import { useMemo, useRef } from "react";
import { useTranslations } from "@acto/web-common/locales";
import type { Kpi } from "@acto/taskpane/core/domain";
import { AnalyticsEvents, kpiToMetric, useTrack } from "@acto/taskpane/core/analytics";
import {
  rowsToAnalysisRows,
  toSchemaKpi,
  toTimeframe,
  trendToRevenueSnapshot,
  useContributeChatScope,
} from "@acto/taskpane/core/chat-envelope";
import type { PeriodSelection } from "@acto/taskpane/field-sales-2/utils/periodSelection";
import {
  ANALYSIS_ROW_LIMIT,
  analysisRowsKind,
  trendItemsToAnalysisRowInputs,
} from "@acto/taskpane/field-sales-2/utils/chatScope";
import { trendsToTrendFilter } from "./productQuickFilter";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { SelectMenu } from "@acto/taskpane/field-sales-2/components/primitives/SelectMenu";
import { useProductGroups } from "@acto/taskpane/field-sales-2/data-access/useProductGroups";
import { useTenantKpiTrend } from "@acto/taskpane/field-sales-2/data-access/useTenantKpiTrend";
import { useProductDrill } from "./ProductDrillContext";
import { ProductAnalysisView } from "./ProductAnalysisView";
import { CustomerBreakdownView } from "./CustomerBreakdownView";
import { AnalysisNavDock } from "./AnalysisNavDock";
import { useDelayedViewTrack } from "./useDelayedViewTrack";
import { buildAnalysisFilters } from "./buildAnalysisFilters";
import { useAnalysisListControls } from "./useAnalysisListControls";
import { CUSTOMERS_OPTION, PRODUCTS_OPTION, useAnalysisBreakdown } from "./useAnalysisBreakdown";

interface TenantAnalysisProps {
  dateRange: { startDate: string; endDate: string };
  isCustomRange: boolean;
  /** The period selection behind dateRange, for the ambient-context timeframe token. */
  selection: PeriodSelection;
  selectedKpi: Kpi;
}

/**
 * Caller-scoped counterpart to [CustomerAnalysis]: the tenant analysis without a client. The
 * breakdown list below the revenue card toggles between product groups (drillable) and customers.
 */
export function TenantAnalysis({ dateRange, isCustomRange, selection, selectedKpi }: TenantAnalysisProps) {
  const tAnalysis = useTranslations("fieldSalesTwo.analysis");
  const tSearch = useTranslations("fieldSalesTwo.search");

  const { stack, drillTo } = useProductDrill();
  const currentGroup = stack.at(-1);

  const { searchValue, onSearchChange, debouncedSearch, trends, filterSlot, reset } = useAnalysisListControls(
    currentGroup?.id,
  );

  const { mode, switchMode, switchModeFromNavDock, docked, dockSearchOpen, setDockSearchOpen, headerSentinelRef } =
    useAnalysisBreakdown({
      defaultMode: "products",
      // Leaving the product hierarchy: drop any drill so the toggle starts at the root list.
      onLeaveProducts: () => drillTo(0),
      resetListControls: reset,
    });

  const track = useTrack();
  // Rapid mode/KPI/drill/filter toggles within the delay window collapse into one representative
  // event: the ref carries the latest state so the fire callback isn't stale by the time it runs.
  const trackingStateRef = useRef({ mode, selectedKpi, dateRange, isCustomRange, currentGroup, trends });
  trackingStateRef.current = { mode, selectedKpi, dateRange, isCustomRange, currentGroup, trends };

  useDelayedViewTrack(() => {
    const s = trackingStateRef.current;
    track(AnalyticsEvents.DASHBOARD_ANALYSIS_VIEWED, {
      sortBy: s.mode === "products" ? "product" : "customer",
      metric: kpiToMetric(s.selectedKpi),
      filters: buildAnalysisFilters(s),
    });
  }, `${mode}:${selectedKpi}`);

  const trend = useTenantKpiTrend({
    kpi: selectedKpi,
    productGroupId: mode === "products" ? currentGroup?.id : undefined,
    dateRange,
    isCustomRange,
  });

  const groups = useProductGroups({
    parents: currentGroup ? [currentGroup.id] : undefined,
    dateRange,
    searchTerm: debouncedSearch,
    selectedKpi,
    trends,
    // Skip the product-group query while the customers breakdown is showing.
    enabled: mode === "products",
  });

  // Ambient context: the tenant analysis selections; the products rows come from the group
  // query here, the customers rows from CustomerBreakdownView (which owns that data).
  const analysisFragment = useMemo(
    () => ({
      selected_kpi: toSchemaKpi(selectedKpi),
      ...toTimeframe(selection),
      ...trendToRevenueSnapshot(trend.raw, selection),
      breakdown: mode,
      trend_filter: trendsToTrendFilter(trends),
      search_term: debouncedSearch || undefined,
      product_drill_path: stack.map((group) => group.name),
      // In customers mode the keys must be OMITTED, not set to undefined — contribute() merges via
      // Object.assign, so an explicit undefined would clobber CustomerBreakdownView's rows.
      ...(mode === "products"
        ? {
            visible_rows_kind: analysisRowsKind(groups.items),
            ...rowsToAnalysisRows(trendItemsToAnalysisRowInputs(groups.items), { limit: ANALYSIS_ROW_LIMIT }),
          }
        : {}),
    }),
    [selectedKpi, selection, trend.raw, mode, trends, debouncedSearch, stack, groups.items],
  );
  useContributeChatScope("analysis", analysisFragment);

  // Switches in place — no scroll-to-top — since the inline title already sits at the top of the list.
  const breakdownToggle = (
    <SelectMenu
      variant="sectionTitle"
      options={[
        { value: PRODUCTS_OPTION, label: tAnalysis("productGroups") },
        { value: CUSTOMERS_OPTION, label: tAnalysis("customers") },
      ]}
      value={mode === "products" ? PRODUCTS_OPTION : CUSTOMERS_OPTION}
      onSelect={(value) => switchMode(value === PRODUCTS_OPTION ? "products" : "customers")}
      triggerLabel={tAnalysis("breakdownToggleLabel")}
    />
  );

  // Page loader is for the first paint only. The trend card and the breakdown lists own their own
  // reload states, so a root-level search reloads the list in place rather than blanking the whole page.
  const isInitialLoad = !currentGroup && trend.isPending;

  return (
    <>
      {/* Surfaces the breakdown toggle + search in the top-nav row once scrolled; only at the root
          (a drilled product level shows its own breadcrumb header instead). */}
      <AnalysisNavDock
        visible={(docked || dockSearchOpen) && !currentGroup}
        mode={mode}
        productsLabel={tAnalysis("productGroups")}
        customersLabel={tAnalysis("customers")}
        toggleLabel={tAnalysis("breakdownToggleLabel")}
        searchPlaceholder={tSearch("placeholder")}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onModeChange={switchModeFromNavDock}
        searchOpen={dockSearchOpen}
        onSearchOpenChange={setDockSearchOpen}
      />
      <PageLayout isLoading={isInitialLoad}>
        {mode === "products" ? (
          <ProductAnalysisView
            kind="revenue"
            rootName={tAnalysis("title")}
            trend={trend}
            groups={groups}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            // The toggle stands in for the list title only at the root; a drilled level shows its own title.
            titleSlot={currentGroup ? undefined : breakdownToggle}
            headerSentinelRef={headerSentinelRef}
            filterSlot={filterSlot}
          />
        ) : (
          <CustomerBreakdownView
            rootName={tAnalysis("title")}
            selectedKpi={selectedKpi}
            trend={trend}
            titleSlot={breakdownToggle}
            dateRange={dateRange}
            searchValue={searchValue}
            debouncedSearch={debouncedSearch}
            onSearchChange={onSearchChange}
            trends={trends}
            filterSlot={filterSlot}
            headerSentinelRef={headerSentinelRef}
          />
        )}
      </PageLayout>
    </>
  );
}
